import { parseSecretManagerField } from "@p0security/integrations-vaulted-credential-shared";
import { assertNever } from "@p0security/util/assertNever";

import type { DockerOrganizationConfig } from "./types.ts";

/** Where an organization's access token is, in the customer's own secret
 * manager: the pointer P0 gives the connector on every call, as the
 * connector's `tokenSecret` schema (p0-connector-docker) takes it. P0 never
 * holds the token. */
export type TokenSecret =
  | { manager: "aws"; region: string; secretId: string }
  | { manager: "gcp"; projectId: string; secretId: string };

/**
 * The partial ARN of the AWS Secrets Manager secret `name`, in `accountId` and
 * `region`: the secret's ARN without the hyphen and six random characters that
 * Secrets Manager adds to its name.
 *
 * Secrets Manager finds a secret by its partial ARN from another account too,
 * which a bare name doesn't allow. The organization's Terraform grants read on
 * it followed by `-??????`. Each value is trimmed, as P0 stores it. P0 takes
 * only regions in AWS's commercial partition, so the partition is `aws`.
 *
 * It follows github-repositories' helper, copied rather than shared so that
 * adding Docker Hub changes no other integration.
 */
const toSecretPartialArn = ({
  accountId,
  name,
  region,
}: {
  accountId: string;
  name: string;
  region: string;
}) =>
  `arn:aws:secretsmanager:${region.trim()}:${accountId.trim()}:secret:${name.trim()}`;

/**
 * Points the connector at the secret that holds an organization's access
 * token: by its partial ARN on AWS, and by its name and project on Google
 * Cloud.
 *
 * Each value is trimmed, as P0 stores it: install checks see the item as the
 * request left it.
 */
export const toTokenSecret = ({
  tokenSecretName,
  vault,
}: Pick<
  DockerOrganizationConfig,
  "tokenSecretName" | "vault"
>): TokenSecret => {
  const locator = parseSecretManagerField(vault);
  const name = tokenSecretName.trim();
  switch (locator.provider) {
    case "aws":
      return {
        manager: "aws",
        region: locator.region.trim(),
        secretId: toSecretPartialArn({
          accountId: locator.accountId,
          name,
          region: locator.region,
        }),
      };
    case "gcp":
      return {
        manager: "gcp",
        projectId: locator.projectId.trim(),
        secretId: name,
      };
    default:
      throw assertNever(locator);
  }
};
